import { DateTime } from 'luxon';

/**
 * Date math for the Schedule grids. Weeks start on Monday in every view, and
 * the month grid is always six full weeks (neighbour days filled in), so its
 * height and its column-to-weekday mapping never change as you page.
 */

export function getWeekStart(date: Date): Date {
  const start = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  start.setDate(start.getDate() - ((start.getDay() + 6) % 7));
  return start;
}

const plusDays = (date: Date, n: number) =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate() + n);

export const getWeekDays = (anchor: Date): Date[] => {
  const start = getWeekStart(anchor);
  return Array.from({ length: 7 }, (_, i) => plusDays(start, i));
};

export const getMonthGridDays = (anchor: Date): Date[] => {
  const start = getWeekStart(new Date(anchor.getFullYear(), anchor.getMonth(), 1));
  return Array.from({ length: 42 }, (_, i) => plusDays(start, i));
};

/** Two letters for a narrow column header: "Dr. Sarah Mitchell" → "SM", "admin" → "AD". */
export const providerInitials = (name: string): string => {
  const words = name.replace(/^(dr\.?|doctor)\s+/i, '').trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return '?';
  const last = words.length > 1 ? words[words.length - 1][0] : words[0][1] ?? '';
  return (words[0][0] + last).toUpperCase();
};

/** Appointment counts per local day (yyyy-MM-dd in `timeZone`) and per vet id, or 'unassigned'. */
export const countByDayAndProvider = (
  appointments: Array<{ date: string; assignedVetId?: string | null }>,
  timeZone: string,
): Record<string, Record<string, number>> => {
  const counts: Record<string, Record<string, number>> = {};
  for (const apt of appointments) {
    const day = DateTime.fromISO(apt.date, { zone: 'utc' }).setZone(timeZone).toFormat('yyyy-MM-dd');
    const provider = apt.assignedVetId || 'unassigned';
    counts[day] = counts[day] ?? {};
    counts[day][provider] = (counts[day][provider] ?? 0) + 1;
  }
  return counts;
};

// The type's colour; an unassigned visit keeps it too (its column already says Unassigned).
export const getAppointmentColor = (appointment: { type: string }, typeColorMap?: Record<string, string>) => {
  // The clinic's colour for this type (Calendar settings), else a default by name.
  const customColor = typeColorMap?.[appointment.type.toLowerCase()];
  if (customColor) {
    return customColor;
  }

  // Default palette by visit type (covers the equine visit-type catalog + legacy types).
  switch (appointment.type.toLowerCase()) {
    case 'checkup':
    case 'general exam':
      return '#43A047'; // Green
    case 'vaccination':
    case 'vaccines':
      return '#2196F3'; // Blue
    case 'dental care':
    case 'dental':
      return '#9C27B0'; // Purple
    case 'emergency':
      return '#E53935'; // Red
    case 'surgery':
      return '#FB8C00'; // Orange
    case 'lameness':
      return '#00897B'; // Teal
    case 'repro':
      return '#D81B60'; // Pink
    case 'recheck':
      return '#3949AB'; // Indigo
    case 'coggins/hc':
      return '#0097A7'; // Cyan
    case 'prepurchase exam':
      return '#5E35B1'; // Deep purple
    case 'medical':
      return '#1E88E5'; // Light blue
    case 'massage/k-tape':
      return '#7CB342'; // Light green
    case 'euthanasia':
      return '#6D4C41'; // Brown
    case 'travel time':
      return '#90A4AE'; // Blue grey
    case 'blocked':
      return '#616161'; // Dark grey
    default:
      return '#757575'; // Grey
  }
};
